/**
 * Branded Sample Preview — allows previewing a global template with real
 * firm + attorney data injected into the sample context.
 *
 * Shows a popover to select firm + optional attorney, then renders the
 * branded preview inline in a dialog.
 */
import { Eye, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { FirmCombobox } from '@/components/firms/FirmCombobox';
import { AttorneySelect } from '@/components/matter/AttorneySelect';
import { PdfRenderer } from '@/components/templates-v2/document-generation/pdf/renderer/PdfFormRenderer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTemplateRenderBrandedSampleMutation } from '@/services/api/templatesV2ServiceEnhanced';

interface BrandedSamplePreviewProps {
  templateId: string;
}

export const BrandedSamplePreview = ({
  templateId,
}: BrandedSamplePreviewProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [firmId, setFirmId] = useState('');
  const [attorneyId, setAttorneyId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const [renderBranded, { isLoading }] =
    useTemplateRenderBrandedSampleMutation();

  const handlePreview = useCallback(async () => {
    if (!firmId) {
      toast.error('Select a firm first');
      return;
    }

    try {
      const blob = await renderBranded({
        templateId,
        firmId,
        attorneyId,
      }).unwrap();

      setPdfBlob(blob);
      setPopoverOpen(false);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to render branded preview');
    }
  }, [templateId, firmId, attorneyId, renderBranded]);

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            <Eye className="h-3.5 w-3.5" />
            Branded Preview
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="end">
          <div className="space-y-3">
            <p className="text-xs font-medium text-zinc-500">
              Preview with real firm & attorney data
            </p>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="branded-firm"
                className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400"
              >
                Firm
              </label>
              <FirmCombobox
                value={firmId}
                onChange={(newFirmId) => {
                  setFirmId(newFirmId);
                  setAttorneyId(null);
                }}
                className="w-full"
              />
            </div>
            {firmId && (
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor="branded-attorney"
                  className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400"
                >
                  Attorney (optional)
                </label>
                <AttorneySelect
                  id="branded-attorney"
                  firmId={firmId}
                  value={attorneyId}
                  onChange={setAttorneyId}
                  placeholder="Auto-select first attorney"
                />
              </div>
            )}
            <Button
              size="sm"
              className="w-full"
              disabled={!firmId || isLoading}
              onClick={handlePreview}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Rendering...
                </>
              ) : (
                <>
                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                  Preview
                </>
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-screen max-w-4xl flex-col p-0">
          <DialogHeader className="flex-none border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
            <DialogTitle className="text-sm font-semibold">
              Branded Sample Preview
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto bg-zinc-50 dark:bg-zinc-900/50">
            {pdfBlob ? (
              <PdfRenderer file={pdfBlob} renderWidth={700} />
            ) : (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
